import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Info, Loader2, MonitorCog, RefreshCw } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { hardware } from "../../lib/hardware";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "../ui/select";

const toOptions = (data) => [
  ...(data.usb || []).map((u) => ({ key: `usb:${u.id}`, label: u.name, opts: { usb: u.id, name: u.name } })),
  ...(data.printers || []).map((p) => ({ key: `prn:${p}`, label: p, opts: { printer: p } })),
];

export const WindowsPrinterPicker = ({ role = "printer" }) => {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState("");
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setData(null);
    try {
      const { data: d } = await api.get("/desktop/printers");
      setData(d);
      const opts = toOptions(d);
      setSel((s) => (opts.some((o) => o.key === s) ? s : (opts.find((o) => /58|pos|vsc|thermal|receipt/i.test(o.label)) || opts[0])?.key || ""));
    } catch (e) {
      setData({ usb: [], printers: [] });
      toast.error(errMsg(e));
    }
  };
  useEffect(() => { load(); }, []);
  const options = data ? toOptions(data) : [];
  const connect = async () => {
    const o = options.find((x) => x.key === sel);
    setBusy(true);
    try {
      await hardware.connect(role, "windows", 9600, o.opts);
      toast.success(`Terhubung: ${o.label}`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-xl border border-primary/40 bg-primary/5 p-3 space-y-2" data-testid="windows-printer-picker">
      <div className="text-sm font-semibold flex items-center gap-2"><MonitorCog className="h-4 w-4 text-primary" />Printer Windows (disarankan)</div>
      <p className="text-xs text-muted-foreground">Pilih <b>Printer USB langsung</b> bila printer dicolok USB (tidak perlu driver), atau printer yang sudah terinstal di Windows.</p>
      <div className="flex flex-wrap gap-2">
        {data === null ? <Loader2 className="h-5 w-5 animate-spin" /> : (
          <Select value={sel} onValueChange={setSel}>
            <SelectTrigger className="w-full sm:w-80 h-9" data-testid="windows-printer-select"><SelectValue placeholder={options.length ? "Pilih printer" : "Printer tidak ditemukan"} /></SelectTrigger>
            <SelectContent>
              {!!data.usb?.length && <SelectGroup><SelectLabel>USB langsung (tanpa driver)</SelectLabel>{data.usb.map((u) => <SelectItem key={u.id} value={`usb:${u.id}`}>{u.name}</SelectItem>)}</SelectGroup>}
              {!!data.printers?.length && <SelectGroup><SelectLabel>Terinstal di Windows</SelectLabel>{data.printers.map((p) => <SelectItem key={p} value={`prn:${p}`}>{p}</SelectItem>)}</SelectGroup>}
            </SelectContent>
          </Select>
        )}
        <Button variant="outline" size="icon" onClick={load} title="Muat ulang daftar" data-testid="windows-printer-refresh"><RefreshCw className="h-4 w-4" /></Button>
        <Button onClick={connect} disabled={!sel || busy} data-testid="windows-printer-connect">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Gunakan Printer Ini</Button>
      </div>
      {data && !options.length && (
        <div className="text-xs rounded-lg bg-warning/10 border border-warning/40 p-2 flex gap-2" data-testid="windows-printer-empty">
          <Info className="h-4 w-4 text-warning shrink-0" />
          Printer belum terdeteksi. Pastikan printer menyala & kabel USB tercolok, tunggu 10 detik lalu klik tombol muat ulang. Bila tetap kosong, instal driver VSC TM-58D / POS-58 dari CD atau situs VSC, lalu muat ulang.
        </div>
      )}
    </div>
  );
};
